import { useParams, useNavigate } from 'react-router';
import { useCallback, useState, useEffect } from 'react';
import { Box, Typography, ButtonBase } from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import KPICard from 'features/IRWorkstation/components/Common/KPICard';
import QuadPanel from 'features/IRWorkstation/components/Common/QuadPanel';
import ActiveRaiseContent from 'features/IRWorkstation/components/Entity/ActiveRaiseContent';
import SearchPicker from 'features/IRWorkstation/components/Search/SearchPicker';
import useActiveRaisePanel from 'features/IRWorkstation/hooks/useActiveRaisePanel';
import AlertsWidget from 'features/IRWorkstation/components/Alerts/AlertsWidget';
import WorkQueueWidget from 'features/IRWorkstation/components/Tasks/WorkQueueWidget';
import fakeData from 'features/IRWorkstation/constants/fakeData';
import buildOverviewKPIs from '../utils/overview';
import { countOverdueTasks } from '../utils/sortWorkQueue';
import { useInfiniteResource } from 'hooks/useInfiniteResource';
import inputSignalsService from 'services/inputSignalsService';
import useSalesforceTasks from 'hooks/useSalesforceTasks';
import useSalesforceRaiseMetrics from 'hooks/useSalesforceRaiseMetrics';
import AlertsDrawer from '../components/Alerts/AlertsDrawer';
import SalesforceTaskDrawer from '../components/Tasks/SalesforceTaskDrawer';
import { useIRWorkstation } from '../contexts/IRWorkstationContext';
{
	/* SS-2816 import NewsFeedWidget from 'features/IRWorkstation/components/NewsFeedWidget'; */
}

const Overview = () => {
	const { pageHeader } = fakeData;
	const { tenantId } = useParams();
	const navigate = useNavigate();

	const [alertsDrawerOpen, setAlertsDrawerOpen] = useState(false);
	const [selectedAlert, setSelectedAlert] = useState(null);
	const [alertStatus, setAlertStatus] = useState('unacknowledged');
	const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
	const [selectedTask, setSelectedTask] = useState(null);

	const [inputSignalStatsLoading, setInputSignalStatsLoading] = useState(true);
	const [criticalAlertsTotalCount, setCriticalAlertsTotalCount] =
		useState(null);

	const handleOpenAlertsDrawer = useCallback((alert) => {
		setSelectedAlert(alert);
		setAlertsDrawerOpen(true);
	}, []);

	const handleCloseAlertsDrawer = useCallback(() => {
		setAlertsDrawerOpen(false);
		setSelectedAlert(null);
	}, []);

	const { tenant } = useIRWorkstation();

	const {
		options,
		selectedEntity,
		selectedEntityId,
		selectedEntityName,
		searchValue,
		setSearchValue,
		onSelect,
		loading,
		pinnedId,
		onPinToggle,
	} = useActiveRaisePanel();

	const handleActiveRaiseClick = useCallback(() => {
		if (selectedEntityId) {
			navigate(`/tenants/${tenantId}/workstation/entities/${selectedEntityId}`);
		}
	}, [selectedEntityId, tenantId, navigate]);

	const fetchAlerts = useCallback(
		(page) =>
			inputSignalsService.getInputSignals(
				{
					event_category: ['alerts'],
					event_status: alertStatus,
				},
				page
			),
		[tenantId, alertStatus]
	);

	// Alerts
	const {
		data: alerts,
		totalCount: alertsTotalCount,
		loading: alertsLoading,
		loadingMore: alertsLoadingMore,
		error: alertsError,
		hasMore: alertsHasMore,
		loadMore: alertsLoadMore,
		refresh: refreshAlerts,
	} = useInfiniteResource({
		fetchFn: fetchAlerts,
	});

	// Distinguish first load from refreshes so alert counts don't disappear
	// while background data is being refreshed
	const alertsInitialLoading = alertsLoading && alertsTotalCount === null;

	// Fetch input signal stats when the tenant or active alert filter changes
	useEffect(() => {
		const fetchInputSignalStats = async () => {
			setInputSignalStatsLoading(true);
			try {
				const response =
					await inputSignalsService.getInputSignalsStats(alertStatus);
				setCriticalAlertsTotalCount(response?.critical_alerts_count || 0);
			} catch (error) {
				console.error('Failed to fetch input signal stats:', error);
			} finally {
				setInputSignalStatsLoading(false);
			}
		};

		fetchInputSignalStats();
	}, [tenantId, alertStatus]);

	const refreshInputSignalStats = useCallback(async () => {
		setInputSignalStatsLoading(true);

		try {
			const response =
				await inputSignalsService.getInputSignalsStats(alertStatus);

			setCriticalAlertsTotalCount(response?.critical_alerts_count ?? 0);
		} catch (error) {
			console.error('Failed to fetch input signal stats:', error);
		} finally {
			setInputSignalStatsLoading(false);
		}
	}, [alertStatus]);

	// Refresh stats when the tenant or active alert filter changes
	useEffect(() => {
		refreshInputSignalStats();
	}, [tenantId, refreshInputSignalStats]);

	// Keep the selected alert and overview data in sync after an alert is updated
	const handleAlertUpdated = useCallback(
		async (updatedAlert) => {
			setSelectedAlert(updatedAlert);

			await Promise.all([refreshAlerts(), refreshInputSignalStats()]);
		},
		[refreshAlerts, refreshInputSignalStats]
	);

	/* SS-2816
	const fetchNewsFeeds = useCallback(
		(page) =>
			inputSignalsService.getInputSignals(
				{
					event_category: ['vdr', 'signal', 'news', 'report'],
					event_status: 'unacknowledged',
				},
				page
			),
		[tenantId]
	);
	*/

	// News & activity feed
	/* SS-2816
	const {
		data: newsFeeds,
		totalCount: newsFeedTotalCount,
		loading: newsFeedLoading,
		loadingMore: newsFeedLoadingMore,
		error: newsFeedError,
		hasMore: newsFeedHasMore,
		loadMore: newsFeedLoadMore,
	} = useInfiniteResource({
		fetchFn: fetchNewsFeeds,
	});

	*/

	// Work Queue
	const {
		tasks: salesforceTasks,
		completedToday,
		loading: workQueueLoading,
		error: workQueueError,
		refetch: refreshTasks,
	} = useSalesforceTasks();

	const handleOpenTaskDrawer = useCallback((task) => {
		setSelectedTask(task);
		setTaskDrawerOpen(true);
	}, []);

	const handleTaskUpdated = useCallback(
		async (updatedTask) => {
			setSelectedTask(updatedTask);
			await refreshTasks();
		},
		[refreshTasks]
	);

	const handleCloseTaskDrawer = useCallback(() => {
		setTaskDrawerOpen(false);
		setSelectedTask(null);
	}, []);

	// Active raise
	const {
		metrics,
		loading: raiseMetricsLoading,
		error: raiseMetricsError,
		refetch,
	} = useSalesforceRaiseMetrics(selectedEntityId);

	const fundProgress = metrics?.pct_to_target;
	const lpsInPipeline = metrics?.fundraising_pipeline?.active_count;
	const fundProgressLoading = raiseMetricsLoading || !metrics;

	const openWorkItemsCount = salesforceTasks.length;
	const openWorkItemsLoading = workQueueLoading;
	const overdueWorkItemsCount = countOverdueTasks(salesforceTasks);

	// Build the KPI card configuration, including values and loading states
	// so each card can independently render a skeleton until its data is ready
	const overviewKPIs = buildOverviewKPIs({
		openWorkItemsCount,
		overdueWorkItemsCount,
		openWorkItemsLoading,
		alertsTotalCount,
		alertStatus,
		criticalAlertsTotalCount,
		alertsLoading: alertsInitialLoading || inputSignalStatsLoading,
		selectedEntityName,
		fundProgress,
		lpsInPipeline,
		fundProgressLoading,
	});

	const handleDataChanged = useCallback(async () => {
		await Promise.all([
			refreshAlerts(),
			refreshTasks(),
			// TODO:
			// refreshNewsFeeds(),
			// refreshMetrics(),
		]);
	}, [refreshAlerts, refreshTasks]);

	const isPinned = selectedEntity?.id === pinnedId;

	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				overflowY: 'auto',
				backgroundColor: 'background.default',
				padding: '12px 20px',
				display: 'flex',
				flexDirection: 'column',
				gap: '14px',
			}}
		>
			{/* page header */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'baseline',
					justifyContent: 'space-between',
				}}
			>
				<Typography sx={{ fontSize: '17px', fontWeight: 700 }}>
					{pageHeader.title}
				</Typography>
				<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
					{pageHeader.meta}
				</Typography>
			</Box>

			{/* kpi strip */}
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: 'repeat(3, 1fr)',
					gap: '10px',
				}}
			>
				{overviewKPIs.map((kpi) => (
					<KPICard key={kpi.label} {...kpi} />
				))}
			</Box>

			{/* quad grid */}
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: '1fr 1fr',
					gridTemplateRows: '1fr 1fr',
					gap: '10px',
					flex: 1,
					minHeight: 0,
				}}
			>
				{/* SS-2816 making this item stretch across two rows - delete to go back to 
				standard quad effect */}
				<Box
					sx={{
						gridColumn: '1',
						gridRow: '1 / span 2',
						minHeight: 0,
						display: 'grid',
					}}
				>
					{/* work queue */}
					<WorkQueueWidget
						onOpenTaskDrawer={handleOpenTaskDrawer}
						salesforceTasks={salesforceTasks}
						completedTodayTasks={completedToday}
						loading={workQueueLoading}
						error={workQueueError}
					/>
				</Box>

				{/* alerts */}
				<AlertsWidget
					onOpenAlertsDrawer={handleOpenAlertsDrawer}
					alerts={alerts}
					loading={alertsLoading}
					loadingMore={alertsLoadingMore}
					error={alertsError}
					hasMore={alertsHasMore}
					loadMore={alertsLoadMore}
					totalCount={alertsTotalCount}
					alertStatus={alertStatus}
					onAlertStatusChange={setAlertStatus}
				/>

				{/* news feed */}
				{/*

				SS-2816. Hiding this to restore later in happier days, 
				when we have more time and nothing hurts.

				<NewsFeedWidget
					onOpenNewsFeedDrawer={onOpenNewsFeedDrawer}
					newsFeeds={newsFeeds}
					totalCount={newsFeedTotalCount}
					loading={newsFeedLoading}
					loadingMore={newsFeedLoadingMore}
					error={newsFeedError}
					hasMore={newsFeedHasMore}
					loadMore={newsFeedLoadMore}
				/>
				*/}

				{/* active raise — badge (confidence/days-to-first-close) and the
					    VDR subtitle are omitted until the backend supplies that data;
					    today raise_metrics returns confidence/first_close_date as null. */}
				<QuadPanel
					title='Active raise'
					subtitle={
						<Box
							display='flex'
							alignItems='center'
							justifyContent='space-between'
							width='100%'
						>
							<SearchPicker
								options={options}
								selected={selectedEntity}
								searchValue={searchValue}
								onChangeSearchValue={setSearchValue}
								onSelect={onSelect}
								loading={loading}
								pinnedId={pinnedId}
							/>
							<ButtonBase
								onClick={() => selectedEntity && onPinToggle(selectedEntity)}
								disabled={!selectedEntity}
								disableRipple
								title={isPinned ? 'Unpin as default' : 'Pin as default'}
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '4px',
									fontSize: '11px',
									fontWeight: 500,
									color: isPinned ? 'status.blue.text' : 'text.secondary',
									backgroundColor: isPinned ? 'status.blue.bg' : 'transparent',
									border: '0.5px solid',
									borderColor: isPinned ? 'primary.main' : 'border.main',
									borderRadius: '6px',
									padding: '4px 9px',
									'&:hover': {
										backgroundColor: 'background.subtle',
										color: 'text.primary',
									},
								}}
							>
								<PushPinIcon
									sx={{ width: 12, height: 12, flexShrink: 0 }}
									fontSize='inherit'
								/>
								{isPinned ? 'Pinned' : 'Pin'}
							</ButtonBase>
						</Box>
					}
				>
					<ActiveRaiseContent
						metrics={metrics}
						loading={raiseMetricsLoading}
						error={raiseMetricsError}
						refetch={refetch}
					/>
					{selectedEntityName && (
						<Box
							onClick={handleActiveRaiseClick}
							sx={{
								margin: '0 15px 13px',
								padding: '8px',
								textAlign: 'center',
								fontSize: '12px',
								fontWeight: 500,
								color: 'status.blue.text',
								border: '0.5px solid',
								borderColor: 'status.blue.border',
								borderRadius: '7px',
								cursor: 'pointer',
								'&:hover': { backgroundColor: 'status.blue.bg' },
							}}
						>
							Enter {selectedEntityName} raise dashboard →
						</Box>
					)}
				</QuadPanel>
			</Box>
			{selectedAlert && (
				<AlertsDrawer
					alert={selectedAlert}
					open={alertsDrawerOpen}
					onClose={handleCloseAlertsDrawer}
					onDataChanged={handleDataChanged}
					onAlertUpdated={handleAlertUpdated}
					tenantId={tenantId}
				/>
			)}
			<SalesforceTaskDrawer
				open={taskDrawerOpen}
				task={selectedTask}
				tenantId={tenantId}
				onClose={handleCloseTaskDrawer}
				onTaskUpdated={handleTaskUpdated}
				salesforceOrgUrl={tenant?.salesforce_org_url}
			/>
		</Box>
	);
};

export default Overview;
